import { createMarkup } from "lib/helpers";

const MAX_WIDTH = "max_width=";

const relativeToAbsolute = (baseUrl: string, relativeUrl: string) => {
    const splitBase = baseUrl.split("/");
    const splitRelative = relativeUrl.split("/");
    splitBase.pop();
    // ignore the current file name (or no string)
    // (ignore if "base" is the current folder without having slash in trail)
    for (let i = 0; i < splitRelative.length; i++) {
        if (splitRelative[i] == ".") {
            continue;
        }
        if (splitRelative[i] == "..") {
            splitBase.pop();
        } else {
            splitBase.push(splitRelative[i]);
        }
    }
    return splitBase.join("/");
};

// Guess best way to fix relative URL.
// 1. When URL starts with `/`, leave it as is. It's absolute url and a valid url
// 2. When URL starts with `http/https`, leave it as is. It's absolute url and a valid url
// 3. When URL starts without 1 & 2, it's a relative URL. Prepend current url OR https
export const guessFixRelativeUrl = (url: string): string => {
    if (typeof url !== "string") {
        return "";
    }
    const shortUrl = isShortUrl(url);
    if (shortUrl) {
        return shortUrl[0];
    }
    if (url.indexOf("mailto:") >= 0) {
        return url.trim();
    }
    if (!/^(?:\/|https?:\/\/)/i.test(url) && url) {
        if (url.indexOf(".") >= 0) {
            url = `https://${url}`;
        } else {
            url = relativeToAbsolute(window.location.href, url);
        }
    }

    return url.trim();
};

export const replaceInternalURL = (html) => {
    return html.replace(/href="(.*?)"/gi, function (searchValue, matchedUrl) {
        const currentHostname = window.location.host;
        try {
            const { hostname, href, origin } = new URL(matchedUrl);
            if (currentHostname === hostname) {
                return `href="${href.replace(origin, "")}"`;
            }
        } catch (error) {
            // no-op
        }
        return searchValue;
    });
};

export const replaceSingleInternalURL = (url: string) => {
    const currentHostname = window.location.host;
    let newUrl = url;
    try {
        const { hostname, href, origin } = new URL(newUrl);
        if (currentHostname === hostname) {
            newUrl = href.replace(origin, "");
        }
    } catch (error) {
        // no-op
    }
    return newUrl;
};

export const replaceEmptyAnchorSpan = (html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = createMarkup(html, { ADD_ATTR: ["target", "allow"], ADD_TAGS: ["iframe"] });
    document.body.appendChild(tempDiv);
    const links = Array.from(tempDiv.getElementsByTagName("a"));
    links.forEach((link) => {
        const href = link.getAttribute("href");
        if (!href || href === "null") {
            const tempSpan = document.createElement("span");
            if (link.id) {
                tempSpan.id = link.id;
            }
            if (link.className) {
                tempSpan.className = link.className;
            }
            if (link.style.length) {
                tempSpan.style.cssText = link.style.cssText;
            }
            tempSpan.innerHTML = createMarkup(link.innerHTML, { ADD_ATTR: ["target"] });
            link.replaceWith(tempSpan);
        }
    });
    tempDiv.remove();
    return tempDiv.innerHTML;
};

export const isLocalUrl = (url) => {
    const fixedUrl = url ? guessFixRelativeUrl(url) : url;
    return !!(fixedUrl?.charAt(0) === "/" || fixedUrl?.includes(window.location.host));
};

export const urlOnClickMobileAppIntercepter = (container: HTMLElement) => {
    // onclick event for mobile team to intercept
    const links = Array.from(container.getElementsByTagName("a"));
    links.forEach((link) => {
        link.onclick = function () {
            const webkit = (window as any).webkit;
            if (webkit && webkit.messageHandlers && webkit.messageHandlers.onPageLinkHandler) {
                webkit.messageHandlers.onPageLinkHandler.postMessage({
                    link: link.href,
                });
                return false;
            }
            return true;
        };
    });
};

export const getImageUrl = (imageUrl: string, width: number, pixelDensity?: number) => {
    const baseDensity = window.devicePixelRatio;
    const densityMultipler = pixelDensity > baseDensity ? pixelDensity : 1.2;
    return imageUrl.replace(/max_width=[0-9]+/i, `${MAX_WIDTH}${Math.ceil(width * densityMultipler)}`);
};

const generateDynamicImageDescriptor = (sizes: number[], imageUrl: string, maxWidth?: number) => {
    const [, widthFromUrl] = imageUrl.split(MAX_WIDTH);
    const parsedWidthFromUrl = parseInt(widthFromUrl);
    const width = parsedWidthFromUrl > maxWidth ? maxWidth : parsedWidthFromUrl;
    return sizes
        .map((size) => {
            return `${getImageUrl(imageUrl, width, size)} ${size}x`;
        })
        .join(",");
};

export const getImageDescriptors = (imageUrl: string, maxWidth?: number) => {
    return generateDynamicImageDescriptor([1, 2, 3], imageUrl, maxWidth);
};

// returns match if it's a DTS brand shorturl (i.e. [gallery])
export const isShortUrl = (url: string) => url.match(/\[(.*?)\]/);
